import { useMemo } from 'react';

import type { SubEnvironment } from '@change-corgi/config/environments';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { TypensenseConfigProps } from 'config/typesense';
import { typesenseConfig } from 'config/typesense';

export function useTypesenseConfig(): TypensenseConfigProps {
	const {
		environment: { getApiEnvironment, getApiDemoEnvironment, getEnvironment },
		resolveUrl,
	} = useUtilityContext();
	const apiEnvironment = getApiEnvironment();
	const environment = apiEnvironment !== 'development' ? apiEnvironment : 'development';

	return useMemo(() => {
		if (getEnvironment() !== 'development' || getApiEnvironment() === 'development') {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-condition
			const env = (getApiDemoEnvironment() as SubEnvironment) || getEnvironment();
			return typesenseConfig[env];
		}
		// in dev when proxying to staging/demo, we want to use the current hostname and port
		// (proxying to the target env is done server-side or within webpack-dev-server)
		const { hostname, port, protocol } = new URL(resolveUrl(''));
		return {
			...typesenseConfig[environment],
			nearestNode: typesenseConfig[environment].nearestNode
				? {
						...typesenseConfig[environment].nearestNode,
						host: hostname,
						port: parseInt(port, 10),
						protocol: protocol.replace(/:$/, ''),
					}
				: undefined,
			nodes: typesenseConfig[environment].nodes.map((node) => ({
				...node,
				host: hostname,
				port: parseInt(port, 10),
				protocol: protocol.replace(/:$/, ''),
			})),
		};
	}, [environment, resolveUrl, getEnvironment, getApiEnvironment, getApiDemoEnvironment]);
}
